 // BYBO APP price replace logic 
  (() => {
    // Run only inside the BYOB product template + BYOB App container to avoid affecting other pages
    const ROOT_SELECTOR = '.template-product-byob #byobApp'
    // Each price wrapper (contains price / compare-at / currency symbol / currency code)
    const WRAPPER_SELECTOR = '.lb-price-wrapper'
    // BYOB "compare at" (original price) selector
    const COMPARE_AT_SELECTOR = '.lb-compare-at-price'
    // BYOB "price" (discounted price) selector
    const PRICE_SELECTOR = '.lb-price'
    // Currency symbol DOM (e.g. $)
    const SYMBOL_SELECTOR = '.lb-currency-symbol'
    // Currency code DOM (e.g. CAD / USD)
    const CODE_SELECTOR = '.lb-currency-code'
    // Cache the last compare-at text we wrote to avoid unnecessary DOM rewrites
    const COMPARE_AT_DATA_KEY = 'byobCompareAtPrice'
    // Cache the last price text we wrote to avoid unnecessary DOM rewrites
    const PRICE_DATA_KEY = 'byobPrice'
    // Cache the original numeric price to prevent re-discounting after MutationObserver reruns
    const ORIGINAL_PRICE_KEY = 'byobOriginalPrice'
    // Cache the original decimals to keep formatting consistent (e.g. 19.9 vs 19.90)
    const ORIGINAL_DECIMALS_KEY = 'byobOriginalPriceDecimals'
    // Cache the original decimal separator (. or ,)
    const ORIGINAL_DECIMAL_SEP_KEY = 'byobOriginalDecimalSep'
    // Cache the original thousands separator (usually , or .)
    const ORIGINAL_THOUSAND_SEP_KEY = 'byobOriginalThousandSep'
    // Cache whether original formatting uses grouping separators (1/0)
    const ORIGINAL_USE_GROUPING_KEY = 'byobOriginalUseGrouping'

    // Infer how many decimals the price text uses (0 when no decimal separator is present)
    const inferDecimals = (text) => {
      const trimmed = (text || '').trim()
      if (!trimmed) return 2


      const lastDot = trimmed.lastIndexOf('.')
      const lastComma = trimmed.lastIndexOf(',')
      const lastSep = Math.max(lastDot, lastComma)
      if (lastSep === -1) return 0

      const fraction = trimmed.slice(lastSep + 1).replace(/[^\d]/g, '')
      if (!fraction) return 0
      if (fraction.length === 1) return 1
      return 2
    }

    // Parse a price text (may include currency symbols/spaces/separators) into a number
    const parsePriceToNumber = (text) => {
      const raw = (text || '').trim()
      if (!raw) return NaN

      const cleaned = raw.replace(/[^\d.,-]/g, '')
      const hasDot = cleaned.includes('.')
      const hasComma = cleaned.includes(',')

      if (hasDot && hasComma) {
        const lastDot = cleaned.lastIndexOf('.')
        const lastComma = cleaned.lastIndexOf(',')
        const decimalSep = lastComma > lastDot ? ',' : '.'

        if (decimalSep === ',') {
          return Number.parseFloat(cleaned.replace(/\./g, '').replace(/,/g, '.'))
        }

        return Number.parseFloat(cleaned.replace(/,/g, ''))
      }

      if (hasComma && !hasDot) {
        return Number.parseFloat(cleaned.replace(/,/g, '.'))
      }

      return Number.parseFloat(cleaned)
    }

    // Infer the original display format (grouping separator / decimal separator / decimals) from raw price text
    const inferNumberFormat = (rawText) => {
      const text = (rawText || '').trim()
      const lastDot = text.lastIndexOf('.')
      const lastComma = text.lastIndexOf(',')
      const lastSepIndex = Math.max(lastDot, lastComma)

      const decimals = inferDecimals(text)
      const decimalSep = decimals > 0 && lastSepIndex !== -1 ? text[lastSepIndex] : ''

      const integerPart = lastSepIndex !== -1 ? text.slice(0, lastSepIndex) : text
      const hasComma = integerPart.includes(',')
      const hasDot = integerPart.includes('.')

      let thousandSep = ''
      if (decimalSep === '.') {
        if (hasComma) thousandSep = ','
      } else if (decimalSep === ',') {
        if (hasDot) thousandSep = '.'
      } else {
        if (hasComma) thousandSep = ','
        else if (hasDot) thousandSep = '.'
      }

      const useGrouping = thousandSep ? 1 : 0
      return { decimals, decimalSep, thousandSep, useGrouping }
    }

    // Format a number back to a string using the original format (keeps .00 and grouping separators)
    const formatNumberLike = (value, format) => {
      const decimals = Math.max(0, Math.min(4, Number(format.decimals) || 0))
      const decimalSep = format.decimalSep || (decimals > 0 ? '.' : '')
      const thousandSep = format.thousandSep || ''
      const useGrouping = Number(format.useGrouping) === 1 && thousandSep

      const numberValue = Number(value)
      if (!Number.isFinite(numberValue)) return ''

      const sign = numberValue < 0 ? '-' : ''
      const fixed = Math.abs(numberValue).toFixed(decimals)
      const parts = fixed.split('.')
      let integerPart = parts[0] || '0'
      const fractionPart = parts[1] || ''

      if (useGrouping) {
        let grouped = ''
        for (let i = integerPart.length; i > 0; i -= 3) {
          const start = Math.max(0, i - 3)
          const chunk = integerPart.slice(start, i)
          grouped = grouped ? `${chunk}${thousandSep}${grouped}` : chunk
        }
        integerPart = grouped
      }

      if (decimals > 0) return `${sign}${integerPart}${decimalSep}${fractionPart}`
      return `${sign}${integerPart}`
    }

    // Find a usable currency DOM (symbol/code) and clone it into the target to avoid moving original nodes
    const findCurrencyElement = (wrapper, selector, options = {}) => {
      const { allowFromTarget = true, targetEl = null } = options
      const candidates = wrapper.querySelectorAll(selector)
      for (const el of candidates) {
        if (el.closest(COMPARE_AT_SELECTOR)) continue
        if (!allowFromTarget && targetEl && targetEl.contains(el)) continue
        return el
      }
      return null
    }

    // Set target element to: symbol DOM + priceText + (space) + code DOM
    const setCurrencyWrappedContent = (wrapper, targetEl, formattedPrice, dataKey, options = {}) => {
      if (targetEl.dataset[dataKey] === formattedPrice) return

      targetEl.dataset[dataKey] = formattedPrice
      const symbolEl = findCurrencyElement(wrapper, SYMBOL_SELECTOR, options)
      const codeEl = findCurrencyElement(wrapper, CODE_SELECTOR, options)

      while (targetEl.firstChild) targetEl.removeChild(targetEl.firstChild)

      if (symbolEl) targetEl.appendChild(symbolEl.cloneNode(true))
      targetEl.appendChild(document.createTextNode(formattedPrice))
      if (codeEl) targetEl.appendChild(document.createTextNode(' '))
      if (codeEl) targetEl.appendChild(codeEl.cloneNode(true))
    }

    // Write compare-at (original price): symbol + original + code
    const setCompareAtContent = (wrapper, compareAtEl, formattedPrice) => {
      setCurrencyWrappedContent(wrapper, compareAtEl, formattedPrice, COMPARE_AT_DATA_KEY, {
        allowFromTarget: false,
        targetEl: compareAtEl,
      })
    }

    // Write price (discounted): symbol + discounted + code
    const setPriceContent = (wrapper, priceEl, formattedPrice) => {
      setCurrencyWrappedContent(wrapper, priceEl, formattedPrice, PRICE_DATA_KEY, {
        allowFromTarget: true,
        targetEl: priceEl,
      })
    }

    // Business rule: compare-at shows original price; price shows original * 0.75 discounted price
    const upsertCompareAt = (wrapper) => {
      // Find the price node inside this wrapper
      const priceEl = wrapper.querySelector(PRICE_SELECTOR)
      if (!priceEl) return

      // Prefer cached original value/format to prevent re-discounting on reruns
      let originalPriceNumber = Number.parseFloat(wrapper.dataset[ORIGINAL_PRICE_KEY])
      let decimals = Number.parseInt(wrapper.dataset[ORIGINAL_DECIMALS_KEY], 10)
      let decimalSep = wrapper.dataset[ORIGINAL_DECIMAL_SEP_KEY] || ''
      let thousandSep = wrapper.dataset[ORIGINAL_THOUSAND_SEP_KEY] || ''
      let useGrouping = Number.parseInt(wrapper.dataset[ORIGINAL_USE_GROUPING_KEY], 10)
      if (!Number.isFinite(originalPriceNumber) || !Number.isFinite(decimals)) {
        // First run: parse original price from the current price text and cache it
        const rawPriceText = (priceEl.textContent || '').trim()
        const parsed = parsePriceToNumber(rawPriceText)
        if (!Number.isFinite(parsed)) {
          return
        }
        originalPriceNumber = parsed
        const format = inferNumberFormat(rawPriceText)
        decimals = format.decimals
        decimalSep = format.decimalSep
        thousandSep = format.thousandSep
        useGrouping = format.useGrouping
        wrapper.dataset[ORIGINAL_PRICE_KEY] = String(originalPriceNumber)
        wrapper.dataset[ORIGINAL_DECIMALS_KEY] = String(decimals)
        wrapper.dataset[ORIGINAL_DECIMAL_SEP_KEY] = decimalSep
        wrapper.dataset[ORIGINAL_THOUSAND_SEP_KEY] = thousandSep
        wrapper.dataset[ORIGINAL_USE_GROUPING_KEY] = String(useGrouping)
      }

      const numberFormat = { decimals, decimalSep, thousandSep, useGrouping }
      const formattedOriginalPrice = formatNumberLike(originalPriceNumber, numberFormat)
      const discountedNumber = originalPriceNumber * 0.75
      const formattedDiscountedPrice = formatNumberLike(discountedNumber, numberFormat)

      // If compare-at exists, update only its content
      const existingCompareAt = wrapper.querySelector(COMPARE_AT_SELECTOR)
      if (existingCompareAt) {
        setCompareAtContent(wrapper, existingCompareAt, formattedOriginalPrice)
        setPriceContent(wrapper, priceEl, formattedDiscountedPrice)
        return
      }

      // If compare-at does not exist, create it and insert before price
      const compareAtEl = document.createElement('b')
      compareAtEl.className = 'lb-compare-at-price'
      setCompareAtContent(wrapper, compareAtEl, formattedOriginalPrice)
      wrapper.insertBefore(compareAtEl, priceEl)
      setPriceContent(wrapper, priceEl, formattedDiscountedPrice)
    }

    // Scan all wrappers under root and apply the rule
    const run = (root) => {
      const wrappers = root.querySelectorAll(WRAPPER_SELECTOR)
      wrappers.forEach(upsertCompareAt)
    }

    // Run once after the BYOB App root exists, then observe DOM changes (BYOB updates asynchronously)
    const init = () => {
      const root = document.querySelector(ROOT_SELECTOR)
      if (!root) return false
      run(root)

      // Simple rAF throttling: multiple mutations in the same frame trigger only one rerun
      let rafScheduled = false
      const observer = new MutationObserver(() => {
        if (rafScheduled) return
        rafScheduled = true
        requestAnimationFrame(() => {
          rafScheduled = false
          run(root)
        })
      })
      observer.observe(root, { childList: true, subtree: true })
      return true
    }

    // BYOB App root may mount late: retry up to 20 times with short polling
    const tryInit = () => {
      let attempts = 0
      const maxAttempts = 20
      const tick = () => {
        attempts += 1
        if (init()) return
        if (attempts >= maxAttempts) return
        setTimeout(tick, 250)
      }
      tick()
    }

    // Try on DOMContentLoaded and on load to cover different load timings
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryInit, { once: true })
    } else {
      tryInit()
    }
    window.addEventListener('load', tryInit, { once: true })
  })()
  
